import { ArrowRight, KeyRound } from "lucide-react";
import { useState, type FormEvent } from "react";
import { enrollPasskey, sendSignInCode, signInWithPasskey, verifySignInCode } from "./auth.js";
import { FacetButton, FacetInput } from "./ui.js";

type AuthStep = "entry" | "code" | "enroll";

function safeReturnTo(value: string | null): string {
  return value?.startsWith("/") && !value.startsWith("//") ? value : "/";
}

function errorMessage(error: unknown): string {
  if (error instanceof DOMException && error.name === "NotAllowedError") {
    return "Passkey sign-in was cancelled. Try again or use an email code.";
  }
  return error instanceof Error ? error.message : "That request could not be completed.";
}

export function AuthPage() {
  const [step, setStep] = useState<AuthStep>("entry");
  const [email, setEmail] = useState(import.meta.env.DEV ? "owner@facet.local" : "");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const returnTo = safeReturnTo(new URLSearchParams(window.location.search).get("returnTo"));

  function finish(): void {
    window.location.assign(returnTo);
  }

  async function usePasskey(): Promise<void> {
    setBusy(true);
    setError(undefined);
    try {
      await signInWithPasskey();
      finish();
    } catch (caught: unknown) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function sendCode(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await sendSignInCode(email.trim().toLowerCase());
      setStep("code");
    } catch (caught: unknown) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function verifyCode(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await verifySignInCode(email.trim().toLowerCase(), code);
      setStep("enroll");
    } catch (caught: unknown) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function addPasskey(): Promise<void> {
    setBusy(true);
    setError(undefined);
    try {
      await enrollPasskey();
      finish();
    } catch (caught: unknown) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-shell">
      <header className="auth-brand">
        <span className="brand-mark" aria-hidden="true">F</span>
        <span>Facet Run</span>
      </header>

      <section className="auth-layout">
        <div className="auth-context">
          <p className="eyebrow">Deployment-aware monitoring</p>
          <h1>Know what still works after the release.</h1>
          <p>Facet connects declared operations to fresh external evidence. Unknown stays unknown until the proof arrives.</p>
          <div className="auth-proof">
            <span aria-hidden="true">◆</span>
            <span>Origin-bound passkeys · revocable sessions · no passwords</span>
          </div>
        </div>

        <div className="auth-form-region" aria-busy={busy}>
          {step === "entry" ? (
            <>
              <div className="auth-heading">
                <p className="eyebrow">Sign in</p>
                <h2>Continue to Facet</h2>
                <p>Your passkey is the direct path. Nothing is emailed unless you ask.</p>
              </div>
              <FacetButton className="auth-passkey" onClick={() => void usePasskey()} disabled={busy}>
                <span className="auth-symbol" aria-hidden="true"><KeyRound /></span>
                <span><strong>Continue with a passkey</strong><small>Face ID, Touch ID, Windows Hello, or security key</small></span>
                <ArrowRight className="auth-passkey-arrow" aria-hidden="true" />
              </FacetButton>
              <div className="auth-divider"><span>or use a one-time code</span></div>
              <form className="auth-form" onSubmit={(event) => void sendCode(event)}>
                <label htmlFor="auth-email">Email address</label>
                <FacetInput
                  id="auth-email"
                  name="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email webauthn"
                  spellCheck={false}
                  placeholder="name@company.com"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
                <FacetButton className="auth-submit" type="submit" disabled={busy}>Email me a code</FacetButton>
              </form>
            </>
          ) : null}

          {step === "code" ? (
            <>
              <div className="auth-heading">
                <p className="eyebrow">{import.meta.env.DEV ? "Check the API log" : "Check your inbox"}</p>
                <h2>Enter the 6-digit code</h2>
                <p>Sent to <strong>{email}</strong>. It expires in five minutes and works once.</p>
              </div>
              <form className="auth-form auth-code-form" onSubmit={(event) => void verifyCode(event)}>
                <label htmlFor="auth-code">Verification code</label>
                <FacetInput
                  className="auth-code"
                  id="auth-code"
                  name="one-time-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  spellCheck={false}
                  pattern="[0-9]{6}"
                  maxLength={6}
                  placeholder="000000"
                  value={code}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
                  required
                  autoFocus
                />
                <FacetButton className="auth-submit" type="submit" disabled={busy || code.length !== 6}>Verify code</FacetButton>
                <FacetButton className="auth-text-button" onClick={() => { setStep("entry"); setCode(""); setError(undefined); }}>Use a different method</FacetButton>
              </form>
            </>
          ) : null}

          {step === "enroll" ? (
            <>
              <div className="auth-heading">
                <p className="eyebrow">Email verified</p>
                <h2>Add a passkey?</h2>
                <p>It is faster next time and resistant to phishing. You can also skip this step.</p>
              </div>
              <div className="auth-enroll-actions">
                <FacetButton className="auth-submit" onClick={() => void addPasskey()} disabled={busy}>Add a passkey</FacetButton>
                <FacetButton className="auth-text-button" onClick={finish}>Continue without one</FacetButton>
              </div>
            </>
          ) : null}

          {error ? <div className="auth-error" role="alert"><span aria-hidden="true">!</span><span>{error}</span></div> : null}
        </div>
      </section>

      <footer className="auth-footer">Passkeys stay on your device. Facet stores only the public credential needed to verify them.</footer>
    </main>
  );
}
